import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, {
  formatApiError, tThreatCategory, tActorType,
  THREAT_CATEGORIES, THREAT_CATEGORY_KEYS, SEVERITY, SEVERITY_KEYS,
  THREAT_STATUS, THREAT_STATUS_KEYS, ACTOR_TYPES, ACTOR_TYPE_KEYS,
} from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AppShell from "@/components/layout/AppShell";
import { SevBadge, ThreatStatusBadge, EmptyState } from "@/components/shared/Badges";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Crosshair, Plus, Search } from "lucide-react";

const EMPTY = {
  name: "", description: "", category: "OTHER", severity: "MEDIUM",
  status: "ACTIVE", source: "", source_reference: "", default_actor_type: "NONE",
};

export default function Threats() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [fStatus, setFStatus] = useState("all");
  const [fSev, setFSev] = useState("all");
  const [fCat, setFCat] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const writable = can("threat:write");

  const { data: threats = [], isLoading } = useQuery({
    queryKey: ["threats", q, fStatus, fSev, fCat],
    queryFn: async () => {
      const params = {};
      if (q) params.q = q;
      if (fStatus !== "all") params.status = fStatus;
      if (fSev !== "all") params.severity = fSev;
      if (fCat !== "all") params.category = fCat;
      return (await api.get("/threats", { params })).data;
    },
  });

  const create = useMutation({
    mutationFn: async () => {
      const payload = { ...form };
      if (payload.default_actor_type === "NONE") payload.default_actor_type = null;
      return (await api.post("/threats", payload)).data;
    },
    onSuccess: () => {
      toast.success("Menace créée");
      setOpen(false);
      qc.invalidateQueries({ queryKey: ["threats"] });
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  return (
    <AppShell
      title="Menaces"
      subtitle={`${threats.length} menace(s) — objet cyber dédié (jamais un NODE)`}
      actions={writable && (
        <Button size="sm" className="rounded-sm gap-2" onClick={() => { setForm(EMPTY); setOpen(true); }} data-testid="create-threat-button">
          <Plus className="h-4 w-4" /> Nouvelle menace
        </Button>
      )}
    >
      <div className="flex flex-wrap items-end gap-3 mb-4" data-testid="threat-filters">
        <div className="relative max-w-xs flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher (nom)…"
            className="pl-9 rounded-sm" data-testid="threat-search-input" />
        </div>
        <FilterSelect label="Statut" value={fStatus} onChange={setFStatus} testid="threat-filter-status"
          options={THREAT_STATUS_KEYS.map((k) => [k, THREAT_STATUS[k].label])} />
        <FilterSelect label="Gravité" value={fSev} onChange={setFSev} testid="threat-filter-severity"
          options={SEVERITY_KEYS.map((k) => [k, SEVERITY[k].label])} />
        <FilterSelect label="Catégorie" value={fCat} onChange={setFCat} testid="threat-filter-category"
          options={THREAT_CATEGORY_KEYS.map((k) => [k, THREAT_CATEGORIES[k]])} />
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground font-mono">Chargement…</p>
      ) : threats.length === 0 ? (
        <EmptyState icon={Crosshair} title="Aucune menace" hint="Créez une menace ou ajustez les filtres." />
      ) : (
        <div className="border border-border rounded-sm bg-card overflow-hidden" data-testid="threats-table">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent border-border">
                <TableHead>Nom</TableHead>
                <TableHead>Catégorie</TableHead>
                <TableHead>Gravité</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead>Acteur (défaut)</TableHead>
                <TableHead className="text-right">Vulnérabilités liées</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {threats.map((t) => (
                <TableRow key={t.id} className="border-border cursor-pointer hover:bg-secondary/40"
                  onClick={() => navigate(`/threats/${t.id}`)} data-testid={`threat-row-${t.id}`}>
                  <TableCell className="font-medium">{t.name}</TableCell>
                  <TableCell className="text-muted-foreground text-xs">{tThreatCategory(t.category)}</TableCell>
                  <TableCell><SevBadge value={t.severity} /></TableCell>
                  <TableCell><ThreatStatusBadge value={t.status} /></TableCell>
                  <TableCell className="text-muted-foreground text-xs">{tActorType(t.default_actor_type) || "—"}</TableCell>
                  <TableCell className="text-right">
                    <Badge variant="outline" className="rounded-sm font-mono text-xs" data-testid={`threat-vuln-count-${t.id}`}>
                      {t.vulnerability_count ?? 0}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card border-border rounded-sm max-w-lg" data-testid="threat-dialog">
          <DialogHeader>
            <DialogTitle className="font-head">Nouvelle menace</DialogTitle>
            <DialogDescription>La gravité est intrinsèque (capacité) — ce n'est jamais un niveau de risque CROS.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <div className="col-span-2">
              <Label className="text-xs">Nom</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="mt-1 rounded-sm" data-testid="threat-name-input" />
            </div>
            <div>
              <Label className="text-xs">Catégorie</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="mt-1 rounded-sm" data-testid="threat-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {THREAT_CATEGORY_KEYS.map((k) => <SelectItem key={k} value={k}>{THREAT_CATEGORIES[k]}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Gravité</Label>
              <Select value={form.severity} onValueChange={(v) => setForm({ ...form, severity: v })}>
                <SelectTrigger className="mt-1 rounded-sm" data-testid="threat-severity-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SEVERITY_KEYS.map((k) => <SelectItem key={k} value={k}>{SEVERITY[k].label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Statut</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="mt-1 rounded-sm" data-testid="threat-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {THREAT_STATUS_KEYS.map((k) => <SelectItem key={k} value={k}>{THREAT_STATUS[k].label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Type d'acteur (optionnel)</Label>
              <Select value={form.default_actor_type} onValueChange={(v) => setForm({ ...form, default_actor_type: v })}>
                <SelectTrigger className="mt-1 rounded-sm" data-testid="threat-actor-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="NONE">Non renseigné</SelectItem>
                  {ACTOR_TYPE_KEYS.map((k) => <SelectItem key={k} value={k}>{ACTOR_TYPES[k]}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Source</Label>
              <Input value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} className="mt-1 rounded-sm" />
            </div>
            <div>
              <Label className="text-xs">Référence source</Label>
              <Input value={form.source_reference} onChange={(e) => setForm({ ...form, source_reference: e.target.value })} className="mt-1 rounded-sm font-mono" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Description</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 rounded-sm" rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Annuler</Button>
            <Button className="rounded-sm" onClick={() => create.mutate()} disabled={!form.name || create.isPending} data-testid="save-threat-button">
              {create.isPending ? "Enregistrement…" : "Enregistrer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}

function FilterSelect({ label, value, onChange, options, testid }) {
  return (
    <div>
      <Label className="text-[11px] uppercase tracking-wider text-muted-foreground font-mono">{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="mt-1 rounded-sm w-[190px]" data-testid={testid}><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Tous</SelectItem>
          {options.map(([k, lbl]) => <SelectItem key={k} value={k}>{lbl}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}
